import { useRouter } from "next/router"
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"

const MAX_HISTORY_LENGTH = 10

// PreviousRouteContext should ideally not be exported. The public interface is meant to include
// only PreviousRouteContextProvider, usePreviousRoute, and useRouteHistory. This is exported solely
// for use in our renderWithProviders function used to ensure our components are unit tested with the
// appropriate context provided.
export const PreviousRouteContext = createContext<string[]>([])

// PreviousRouteContextProvider wraps the PreviousRouteContext provider and implements the logic
// for tracking the previous route. While the implementation here is concise, it is complex, relying
// on a deep understanding of router events, state updates, and React’s rendering cycle.
// We subscribe to Next.js's routeChangeComplete event and store a bounded stack of visited routes.
// This ensures the history is updated only after navigation completes, avoiding render-timing
// edge cases and enabling accurate access to the previous route.
export function PreviousRouteContextProvider({ children }: PropsWithChildren) {
  const router = useRouter()
  const { asPath, isReady, events: routerEvents } = router

  const [history, setHistory] = useState<string[]>([])

  useEffect(() => {
    if (!isReady) return

    const handleRouteChange = (path: string) => {
      setHistory((prevHistory) => {
        // Only add if different from most recent entry
        if (prevHistory[0] === path) {
          return prevHistory
        }

        // Add to front
        const newHistory = [path, ...prevHistory]

        // Drop oldest
        if (newHistory.length > MAX_HISTORY_LENGTH) {
          newHistory.pop()
        }

        return newHistory
      })
    }

    handleRouteChange(asPath)

    // Subscribe
    routerEvents.on("routeChangeComplete", handleRouteChange)

    // Cleanup
    return () => {
      routerEvents.off("routeChangeComplete", handleRouteChange)
    }
  }, [isReady, asPath, routerEvents])

  return (
    <PreviousRouteContext.Provider value={history}>
      {children}
    </PreviousRouteContext.Provider>
  )
}

export function useRouteHistory(): string[] {
  return useContext(PreviousRouteContext)
}

export function usePreviousRoute(): string | undefined {
  // Index [0] is current, [1] is previous
  return useRouteHistory()[1]
}
